import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Rocket, Calendar, Trash2, AlertTriangle, Shield, BarChart3, KeyRound, ChevronRight
} from 'lucide-react';

// Update popup - will show for admin users for one week only (Jan 27 - Feb 3, 2026)
const UPDATE_START_DATE = new Date('2026-01-27T00:00:00Z');
const UPDATE_END_DATE = new Date('2026-02-03T23:59:59Z');

export default function UpdatePopup({ user }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Only show for Admin users
    if (user?.role !== 'Admin') return;
    
    // Check if within the one-week window
    const now = new Date();
    if (now < UPDATE_START_DATE || now > UPDATE_END_DATE) return;
    
    // Show the popup on every login during the week
    setIsOpen(true);
  }, [user]);

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  const updates = [
    {
      icon: <Rocket className="h-5 w-5" />,
      title: "Deliveries Page – Performance Boost",
      emoji: "🚀",
      color: "text-green-600 bg-green-100",
      items: [
        "Big speed win for daily users",
        "Default filter changed from \"All Time\" → \"Today\" (instant load)",
        "Server-side filtering implemented (no more loading 500+ records)",
        "Period filter now queries the database directly",
        "Deliveries now sorted most recent first"
      ]
    },
    {
      icon: <Calendar className="h-5 w-5" />,
      title: "Deliveries Page – Calendar Date Picker",
      emoji: "📅",
      color: "text-blue-600 bg-blue-100",
      items: [
        "New \"Pick a date\" button with calendar icon",
        "Calendar popup to select a specific date",
        "Page title dynamically updates (e.g. \"Deliveries for Monday, 22 December 2025\")",
        "Clear (X) button resets view back to period filter",
        "Past dates show accepted deliveries only"
      ]
    },
    {
      icon: <Trash2 className="h-5 w-5" />,
      title: "Deliveries Page – Bulk Delete (Admin Only)",
      emoji: "🗑️",
      color: "text-red-600 bg-red-100",
      items: [
        "Multi-select checkboxes added (Admin only)",
        "Select All option in table header",
        "Red Delete (N) button appears when items are selected",
        "Selection banner shows item count and Clear Selection",
        "Selected rows highlighted in blue",
        "Only deletable statuses show checkboxes: Draft · Pending · Waiting for Driver · Overweight"
      ]
    },
    {
      icon: <AlertTriangle className="h-5 w-5" />,
      title: "Deliveries Page – Admin Delete for OVERWEIGHT",
      emoji: "⚠️",
      color: "text-amber-600 bg-amber-100",
      items: [
        "Admin-only delete option for overweight deliveries",
        "Confirmation dialog shown before deletion",
        "Non-admin users cannot delete overweight entries"
      ]
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: "Backup System Enhancements",
      emoji: "🔐",
      color: "text-purple-600 bg-purple-100",
      items: [
        "Backup schedule changed: 2:00 AM → 6:00 PM (UK GMT)",
        "Retention extended: 30 days → 60 days",
        "Auto-delete backups older than 60 days",
        "Expiry warning notifications sent 2 weeks before expiry",
        "Backups page now shows: Days remaining, Orange warning badge, Expiry date column, 60 Days retention info"
      ]
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: "Admin Dashboard – Delivery Statistics & Export",
      emoji: "📊",
      color: "text-indigo-600 bg-indigo-100",
      items: [
        "New delivery counters added: Daily · Weekly · Monthly · Yearly",
        "Export delivery data for reporting and analysis"
      ]
    },
    {
      icon: <KeyRound className="h-5 w-5" />,
      title: "Password Reset Improvements",
      emoji: "🔑",
      color: "text-slate-600 bg-slate-100",
      items: [
        "Password reset now supports recovery key",
        "Faster and more reliable account recovery"
      ]
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden [&>button]:hidden" data-testid="update-popup">
        {/* Header Image */}
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_4e942ebe-87e9-472d-a9f7-b80b3e8e29e4/artifacts/ygfxus2x_image.png"
            alt="What's New in This Update"
            className="w-full h-auto"
          />
        </div>

        {/* Content */}
        <ScrollArea className="max-h-[50vh] px-6 pb-6">
          <div className="space-y-4 pt-4">
            {updates.map((update, index) => (
              <div 
                key={index} 
                className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors"
              >
                {/* Section Header */}
                <div className={`flex items-center gap-3 px-4 py-3 ${update.color.split(' ')[1]} border-b border-slate-100`}>
                  <div className={`p-1.5 rounded-lg bg-white/80 ${update.color.split(' ')[0]}`}>
                    {update.icon}
                  </div>
                  <h3 className={`font-semibold ${update.color.split(' ')[0]}`}>
                    {update.title} {update.emoji}
                  </h3>
                </div>
                
                {/* Section Items */}
                <div className="p-4 bg-white">
                  <ul className="space-y-2">
                    {update.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-sm text-slate-700">
                        <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            These updates are live now. Enjoy the improvements!
          </p>
          <Button onClick={handleClose} data-testid="dismiss-update-btn">
            Got it, thanks!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
